'use client';

import { CheckCircle2, Loader2, Upload, XCircle } from 'lucide-react';
import { useRef, useState } from 'react';

export interface GseImportFileResult {
	name: string;
	pod?: string;
	tipologia?: string;
	mese?: number;
	anno?: number;
	rows?: number;
	skipped?: string;
	error?: string;
}

export interface GseImportCardProps {
	busy: boolean;
	/** Esito dell'ultimo import (null = nessun import ancora eseguito) */
	results: GseImportFileResult[] | null;
	totalWritten: number | null;
	/** Avvia l'import dei file selezionati (.xlsx e/o .zip) */
	onImport: (files: File[]) => void;
}

/**
 * Import massivo dei workbook GSE «Dett_Misura» (misure ufficiali PRELEVATA /
 * IMMESSA): selezione multipla di file xlsx oppure zip di xlsx, con esito per
 * file. I dati confluiscono nella sorgente storica ufficiale GSE.
 */
export function GseImportCard({ busy, results, totalWritten, onImport }: GseImportCardProps) {
	const inputRef = useRef<HTMLInputElement>(null);
	const [selected, setSelected] = useState<File[]>([]);

	function handleImport() {
		if (selected.length > 0) onImport(selected);
	}

	return (
		<div className="card">
			<div className="card-head">
				<span className="card-title">
					<Upload className="mr-1 inline h-3.5 w-3.5 text-accent-green" />
					Import misure GSE (xlsx)
				</span>
				<span className="card-badge badge-green">DATI UFFICIALI</span>
			</div>

			<p className="mb-3 text-[12px] text-text-muted">
				Carica i file «Dett_Misura» scaricati dal portale GSE - più xlsx insieme o uno zip che li
				contiene. Ogni file viene abbinato al POD corrispondente della CER e la curva oraria diventa
				la sorgente ufficiale nella riconciliazione dello storico (priorità massima).
			</p>

			<div className="flex flex-wrap items-center gap-2">
				<input
					ref={inputRef}
					type="file"
					multiple
					accept=".xlsx,.zip"
					disabled={busy}
					onChange={(e) => setSelected(Array.from(e.target.files ?? []))}
					aria-label="Carica file Dett_Misura GSE (xlsx o zip)"
					className="text-[13px] text-text-primary file:mr-2 file:rounded file:border file:border-border file:bg-background file:px-2 file:py-1 file:text-[13px] file:text-text-primary"
				/>

				<button
					type="button"
					onClick={handleImport}
					disabled={busy || selected.length === 0}
					className="inline-flex items-center gap-1.5 rounded border border-accent-green/50 bg-accent-green/10 px-3 py-1 text-[13px] text-accent-green transition-colors hover:bg-accent-green/20 disabled:opacity-50"
				>
					{busy ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
					{busy
						? 'Import in corso…'
						: `Importa${selected.length > 0 ? ` (${selected.length} file)` : ''}`}
				</button>
			</div>

			{results && (
				<div className="mt-3 space-y-1">
					{totalWritten != null && (
						<p className="text-[12px] font-medium text-text-primary">
							{totalWritten} letture orarie scritte
						</p>
					)}
					<ul className="space-y-0.5">
						{results.map((r, i) => {
							const failed = !!r.error || !!r.skipped;
							return (
								<li
									key={`${r.name}-${i}`}
									className="flex items-start gap-1.5 text-[12px] text-text-muted"
								>
									{failed ? (
										<XCircle className="mt-0.5 h-3 w-3 shrink-0 text-accent-red" />
									) : (
										<CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-accent-green" />
									)}
									<span className="min-w-0 break-all">
										<span className="text-text-primary">{r.name}</span>
										{r.pod && ` · ${r.pod}`}
										{r.tipologia && ` · ${r.tipologia}`}
										{r.mese != null && r.anno != null && ` · ${r.mese}/${r.anno}`}
										{!failed && r.rows != null && ` · ${r.rows} ore`}
										{r.skipped && ` - ${r.skipped}`}
										{r.error && ` - ${r.error}`}
									</span>
								</li>
							);
						})}
					</ul>
				</div>
			)}

			<p className="mt-2 text-[11px] italic text-text-muted">
				Il POD viene letto dal foglio Riepilogo, non dal nome del file. I file con POD non presente
				nella CER vengono saltati e segnalati.
			</p>
		</div>
	);
}
